import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, resolveTenantSlug } from "../lib/api";
import { tokenStore, tenantStore } from "../lib/tokenStore";
import { onAppResume, refreshFcmTokenAfterLogin } from "./../lib/capacitor";
import { removeWebPushSubscription } from "./../lib/webPush";
import { clearCartSnapshots } from "./../lib/offlineCart";

const AuthCtx = createContext({ user: null, tenant: null, loading: true });

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // iter61 — boot order:
        //   1) ALWAYS try ``/auth/me`` first — the httpOnly cookie may
        //      already authenticate us silently, even if localStorage
        //      is empty (fresh device that logged in via cookie OR
        //      a tab where the user pasted no link).
        //   2) If that 401s, fall back to localStorage Bearer (legacy
        //      users who haven't re-logged-in since the migration).
        //   3) If both fail → genuinely logged out.
        try {
            const { data } = await api.get("/auth/me");
            // Cross-tenant safety: if the JWT was issued for a tenant
            // that doesn't match the URL the user is currently on,
            // log them out so the wrong tenant's data never renders.
            // SUPER_ADMIN bypass — they roam across tenants by design.
            //
            // iter179 — this drops the SESSION only. The tenant the tab
            // is on (``?tenant=``, a custom domain, the app's own link)
            // stays exactly where it is: it used to be erased here too,
            // and the next lookup then fell through to the default
            // tenant — a session for the wrong store quietly moved the
            // visitor to a third one.
            //
            // NOTE: ``resolveTenantSlug`` is sticky (persisted via
            // sessionStorage — iter146, was localStorage — from the
            // original ``?tenant=X`` link), so
            // a tenant-admin who logged in via ``?tenant=foo`` keeps
            // resolving to ``foo`` across SPA navigation — they only
            // get auto-logged-out when they EXPLICITLY visit a
            // different tenant URL (subdomain or ``?tenant=other``).
            const hostSlug = resolveTenantSlug();
            const tenantSlug = data?.tenant?.slug;
            const role = data?.user?.role;
            if (
                tenantSlug && hostSlug
                && tenantSlug !== hostSlug
                && role !== "SUPER_ADMIN"
                && !tokenStore.isImpersonation()
            ) {
                tokenStore.clear();
                setUser(null);
                setTenant(null);
                setLoading(false);
                return;
            }
            // Keep the persisted slug in lock-step with the JWT tenant
            // so the API interceptor sends the correct X-Tenant-Slug
            // even after the user drops the ``?tenant=X`` query param.
            if (tenantSlug) tenantStore.setPersistent(tenantSlug);
            setUser(data.user);
            setTenant(data.tenant);
        } catch (err) {
            // Clear the session ONLY when the backend actually said this session is not valid.
            //
            // This runs on every return to the foreground (see the resume effect below), which is precisely
            // when the network is least reliable — a phone waking from Doze, a Wi-Fi↔mobile handoff. It used
            // to clear on ANY response, so one 502 during a deploy, or a 500, ended a perfectly good session
            // and cost an OTP to get back. Only 401 and 403 mean "not authenticated": on ``/auth/me`` the sole
            // 403 reachable is the super-admin lockout in auth.py, which IS a session-invalid condition, so
            // that behaviour is preserved exactly. 5xx, 404, timeouts and outright network failures leave the
            // user signed in to retry — the token is untouched and still expires server-side on schedule.
            //
            // ``sessionCheckUnreachable`` (set by the api.js interceptor) marks a 401 we could NOT verify
            // because /auth/refresh was unreachable. That 401 is expected — the access cookie had expired —
            // and the refresh that would have renewed it never got an answer, so it is no evidence the
            // session is over.
            const status = err?.response?.status;
            if (!err?.sessionCheckUnreachable && (status === 401 || status === 403)) {
                tokenStore.clear();
                setUser(null);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Android navigation bug fix (user-reported) — if `refresh()`'s
    // request was in flight when the OS suspended the WebView's network
    // (app backgrounded, Doze mode, etc), resuming the app should give
    // it another chance to complete instead of leaving `loading` (and
    // therefore `RequireAuth`'s FullPageLoader, which gates every
    // authenticated route) stuck forever. No-op on web/no-op if the
    // first `refresh()` already succeeded — `loading` is already false
    // by then, so this just re-validates the session on resume, which
    // is cheap and harmless.
    useEffect(() => {
        let unsubscribe = () => {};
        onAppResume(() => { refresh(); }).then((fn) => { unsubscribe = fn; });
        return () => unsubscribe();
    }, [refresh]);

    const login = (token, u, t) => {
        tokenStore.set(token);
        // Pin the persisted tenant slug to the user's JWT tenant on
        // login so subsequent API calls send the matching
        // X-Tenant-Slug header — independent of any stale storage or
        // dropped ``?tenant=`` query param.
        if (t?.slug) tenantStore.setPersistent(t.slug);
        setUser(u);
        setTenant(t);
        // If we're in a Capacitor app and we already have an FCM token in
        // memory, re-upload it now that we have a JWT so the backend can
        // associate the device with this user.
        refreshFcmTokenAfterLogin();
    };

    const logout = async () => {
        // iter61 — also call backend ``POST /auth/logout`` to clear the
        // httpOnly cookies + drop FCM tokens. Fire-and-forget: a network
        // failure here can't leave the user stuck logged-in because
        // ``tokenStore.clear()`` below removes the only client-side
        // copy of their Bearer token.
        // Drop this browser's web-push subscription first, while the request still carries a valid session —
        // otherwise the next person signing in on a shared machine keeps receiving the last user's orders.
        try {
            await removeWebPushSubscription();
        } catch {
            // best-effort — never block logout on it
        }
        // A saved offline cart (Location Service Engine) belongs to this account only — never to the next user.
        clearCartSnapshots(resolveTenantSlug());
        try {
            // iter181 — name the device. With no body the server removed the account's registration on EVERY
            // device, so signing out in a browser silenced the phone as well. The app sends its own token; a
            // browser (which has no native token) says so, and its web-push subscription was removed just above.
            const deviceToken = typeof window !== "undefined" ? window.__bizilFcmToken : null;
            await api.post("/auth/logout", deviceToken ? { token: deviceToken } : { scope: "web" });
        } catch {
            // best-effort — proceed with client-side cleanup regardless
        }
        tokenStore.clear();
        // iter179 — logout ends the SESSION, never the tenant context. The tenant this tab is on was established
        // by the address that opened it (``?tenant=``, a custom domain, the app's own link) and is all that keeps
        // the next request, the login screen and a reload on the same store once that address is no longer in the
        // URL. Erasing it here sent a signed-out visitor to the default tenant, where signing in again created an
        // account in the wrong store. It is per-tab (sessionStorage), so a new tab or app launch still starts clean.
        setUser(null);
        setTenant(null);
    };

    return <AuthCtx.Provider value={{ user, tenant, loading, login, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
